import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import SEO from "@/components/SEO";

/**
 * SubscribeAction — landing page for both /subscribe/confirm/:token and
 * /subscribe/unsubscribe/:token. The `mode` prop selects which endpoint to
 * hit. Copy is restrained; the point is not to convert but to acknowledge.
 */
export default function SubscribeAction({ mode = "confirm" }) {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading", email: null, error: null });
  const [resendEmail, setResendEmail] = useState("");
  const [resendState, setResendState] = useState("idle"); // idle | sending | done

  useEffect(() => {
    if (!token) {
      setState({ status: "error", error: "Missing token." });
      return;
    }
    const endpoint = mode === "confirm"
      ? `/subscribe/confirm/${token}`
      : `/subscribe/unsubscribe/${token}`;
    api.get(endpoint)
      .then(({ data }) => setState({ status: "ok", email: data.email, error: null }))
      .catch((err) => {
        const detail = formatApiErrorDetail(err.response?.data?.detail);
        setState({
          status: "error",
          error: detail || "Something went wrong. Please try again or write to the firm.",
        });
      });
  }, [token, mode]);

  const kicker =
    state.status === "loading"
      ? "One moment"
      : state.status === "error"
        ? "We hit a snag"
        : isConfirm
          ? "Subscription Confirmed"
          : "You have been removed";
  const isConfirm = mode === "confirm";
  const okHeadline = isConfirm
    ? "Welcome to the mailing list."
    : "Your address has been removed.";
  const okBody = isConfirm
    ? "You will receive future editorial dispatches from Lone Star Counsel. Nothing to do from here — sit back and expect the next quarterly letter."
    : "You will not receive further dispatches from the firm. If this was accidental, you may re-subscribe from the Journal or Contact page.";

  return (
    <div className="bg-ivory min-h-screen flex flex-col" data-testid="subscribe-action-page">
      <SEO
        title={`${isConfirm ? "Confirm" : "Unsubscribe"} — Lone Star Counsel`}
        description="Mailing-list management for Lone Star Counsel editorial dispatches."
        path={`/subscribe/${mode}/${token || ""}`}
      />
      <section className="panel-navy grain grain-dark pt-40 pb-24 md:pt-48 md:pb-32 flex-1">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Star size={11} className="text-brass fill-brass mx-auto" />
          <div className="mt-6 text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
            {kicker}
          </div>

          {state.status === "loading" && (
            <h1 className="mt-10 font-display text-4xl md:text-5xl text-ivory leading-[1.05]">
              Processing your request…
            </h1>
          )}

          {state.status === "ok" && (
            <>
              <h1
                className="mt-10 font-display text-4xl md:text-6xl text-ivory leading-[1.05]"
                data-testid="subscribe-action-headline"
              >
                {okHeadline}
              </h1>
              <p className="mt-8 max-w-xl mx-auto font-cormorant italic text-lg md:text-xl text-ivory/85 leading-snug">
                {okBody}
              </p>
              {state.email && (
                <div className="mt-6 text-[10px] uppercase tracking-[0.32em] text-brass/80">
                  {state.email}
                </div>
              )}
            </>
          )}

          {state.status === "error" && (
            <>
              <h1
                className="mt-10 font-display text-4xl md:text-5xl text-ivory leading-[1.05]"
                data-testid="subscribe-action-error"
              >
                We could not complete that request.
              </h1>
              <p className="mt-8 max-w-xl mx-auto font-cormorant italic text-lg text-ivory/80 leading-snug">
                {state.error}
              </p>

              {/* Resend-the-confirmation form — only surfaces on the
                  confirm route (unsubscribe errors don't need resend). */}
              {isConfirm && (
                <form
                  className="mt-12 max-w-md mx-auto"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!resendEmail.trim()) return;
                    setResendState("sending");
                    try {
                      await api.post("/subscribe", {
                        email: resendEmail.trim(),
                        source: "state_of_the_firm",
                      });
                      setResendState("done");
                      toast.success("A fresh confirmation link is on its way.");
                      setResendEmail("");
                    } catch (err) {
                      setResendState("idle");
                      toast.error(
                        formatApiErrorDetail(err.response?.data?.detail) ||
                          "Could not resend the confirmation."
                      );
                    }
                  }}
                  data-testid="resend-confirmation-form"
                >
                  <div className="hairline-top pt-6 text-center">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
                      Or — send me a fresh link
                    </div>
                    <p className="mt-3 font-cormorant italic text-ivory/70 text-base leading-snug">
                      Enter the email you originally used. A new confirmation link will arrive.
                    </p>
                    <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                      <input
                        type="email"
                        required
                        placeholder="your.email@firm.com"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        className="field !mb-0 !bg-transparent !text-ivory !border-ivory/30 placeholder:text-ivory/40 focus:!border-brass text-center sm:text-left"
                        data-testid="resend-confirmation-email"
                        disabled={resendState !== "idle"}
                      />
                      <button
                        type="submit"
                        className="btn-brass inline-flex items-center gap-2 disabled:opacity-40 shrink-0"
                        data-testid="resend-confirmation-submit"
                        disabled={resendState !== "idle"}
                      >
                        <Star size={11} className="fill-current" />
                        {resendState === "sending"
                          ? "Sending\u2026"
                          : resendState === "done"
                            ? "Check your inbox"
                            : "Resend link"}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </>
          )}

          <div className="mt-14 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/"
              className="btn-brass inline-flex items-center gap-2"
              data-testid="subscribe-action-home"
            >
              <Star size={11} className="fill-current" />
              Return to the front page
            </Link>
            {isConfirm && state.status === "ok" && (
              <Link
                to="/journal"
                className="text-[11px] uppercase tracking-[0.32em] text-ivory/70 hover:text-brass transition-colors font-medium border-b border-brass/40 hover:border-brass pb-1"
              >
                Read the Journal &rarr;
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
